import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CrudPage from './shared/CrudPage';
import {
  getTareas, createTarea, updateTarea, deleteTarea,
  getCategorias, setTestMode, unsetTestMode, uploadAdminFile,
} from '../../api/adminClient';

export default function AdminTareas() {
  const navigate = useNavigate();
  const [catList, setCatList] = useState([]);
  const [selLenguaje, setSelLenguaje] = useState(null); // { id, label }
  const [selCategoria, setSelCategoria] = useState(null); // { id, label }
  const [toggling, setToggling] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    getCategorias()
      .then((rows) => setCatList(rows.filter((c) => !c.baja)))
      .catch(() => {});
  }, []);

  const lenguajes = useMemo(() => {
    const seen = new Map();
    catList.forEach((c) => {
      if (!seen.has(c.id_lenguaje)) seen.set(c.id_lenguaje, c.lenguaje);
    });
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [catList]);

  const catsPorLenguaje = useMemo(() =>
    selLenguaje ? catList.filter((c) => c.id_lenguaje === selLenguaje.id) : [],
    [catList, selLenguaje]
  );

  const catOpts = useMemo(() =>
    catList.map((c) => ({ value: c.ID, label: `${c.lenguaje} — ${c.Descripcion}` })),
    [catList]
  );

  const filterFn = useMemo(() => {
    if (selCategoria) return (row) => String(row.id_tipotest) === String(selCategoria.id);
    if (selLenguaje) {
      const ids = new Set(catsPorLenguaje.map((c) => String(c.ID)));
      return (row) => ids.has(String(row.id_tipotest));
    }
    return null;
  }, [selCategoria, selLenguaje, catsPorLenguaje]);

  const handleTestModeToggle = useCallback(async (row) => {
    setToggling(row.ID);
    try {
      if (row.test_mode) await unsetTestMode('tarea', row.ID);
      else               await setTestMode('tarea', row.ID);
      setRefresh((n) => n + 1);
    } catch (e) {
      alert(e.message);
    } finally {
      setToggling(null);
    }
  }, []);

  const pill = 'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer';
  const pillIdle = 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-violet-100 hover:text-violet-700 hover:border-violet-300';

  const nav = (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-1 text-sm flex-wrap">
        <button
          type="button"
          onClick={() => { setSelLenguaje(null); setSelCategoria(null); }}
          className={`font-medium border-0 bg-transparent p-0 cursor-pointer ${!selLenguaje ? 'text-violet-700' : 'text-slate-400 hover:text-violet-600'}`}
        >
          Tareas
        </button>
        {selLenguaje && (
          <>
            <span className="text-slate-300">›</span>
            <button
              type="button"
              onClick={() => setSelCategoria(null)}
              className={`font-medium border-0 bg-transparent p-0 cursor-pointer ${!selCategoria ? 'text-violet-700' : 'text-slate-400 hover:text-violet-600'}`}
            >
              {selLenguaje.label}
            </button>
          </>
        )}
        {selCategoria && (
          <>
            <span className="text-slate-300">›</span>
            <span className="font-medium text-violet-700">{selCategoria.label}</span>
          </>
        )}
      </div>

      {!selLenguaje && (
        <div className="flex flex-wrap gap-1.5">
          {lenguajes.map((l) => (
            <button key={l.id} type="button" onClick={() => setSelLenguaje(l)} className={`${pill} ${pillIdle}`}>
              {l.label}
            </button>
          ))}
        </div>
      )}

      {selLenguaje && !selCategoria && (
        <div className="flex flex-wrap gap-1.5">
          {catsPorLenguaje.map((c) => (
            <button
              key={c.ID}
              type="button"
              onClick={() => setSelCategoria({ id: c.ID, label: c.Descripcion })}
              className={`${pill} ${pillIdle}`}
            >
              {c.Descripcion}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <CrudPage
      title="Tareas"
      idKey="ID"
      columns={[
        { key: 'ID', label: 'ID' },
        {
          key: 'Descripcion',
          label: 'Descripción',
          primary: true,
          render: (v, row) => (
            <div>
              <button
                type="button"
                onClick={() => navigate(`/admin/tests?tareaId=${row.ID}`)}
                className="text-left font-semibold text-violet-700 hover:text-violet-900 hover:underline bg-transparent border-0 cursor-pointer p-0"
              >
                {v}
              </button>
              <div className="font-normal text-xs text-slate-400 mt-0.5">{row.lenguaje} → {row.categoria}</div>
            </div>
          ),
        },
        {
          key: 'total_tests',
          label: 'Ejercicios',
          render: (v) => (
            <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              {v ?? 0}
            </span>
          ),
        },
        {
          key: 'rutaImagen',
          label: 'Imagen',
          render: (v) => v
            ? <img src={v} alt="" className="w-9 h-9 rounded-lg object-cover border border-slate-200" />
            : '',
        },
        { key: 'baja', label: 'Baja', render: (v) => v ? 'Sí' : 'No' },
        {
          key: 'test_mode',
          label: 'Testing',
          render: (v, row) => (
            <button
              type="button"
              disabled={toggling === row.ID}
              onClick={() => handleTestModeToggle(row)}
              className={`px-2 py-0.5 rounded text-xs font-medium border-0 cursor-pointer transition-colors ${
                v
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
              }`}
            >
              {toggling === row.ID ? '...' : v ? 'Testing' : 'Normal'}
            </button>
          ),
        },
      ]}
      fields={[
        { key: 'id_tipotest',  label: 'Proceso',      type: 'select', options: catOpts, required: true },
        { key: 'Descripcion',  label: 'Descripción',  required: true },
        { key: 'marcoteorico', label: 'Marco teórico', type: 'textarea' },
        { key: 'observaciones',label: 'Observaciones', type: 'textarea' },
        { key: 'concluscion',  label: 'Conclusión',   type: 'textarea' },
        {
          key: 'rutaImagen',
          label: 'Imagen',
          type: 'image',
          onUpload: (file) => uploadAdminFile(file, 'tareas'),
          hint: (row) => (row?.ID ? `t_${row.ID}.png` : null),
        },
        { key: 'baja',         label: 'Baja',         type: 'checkbox' },
      ]}
      fetchAll={useCallback(() => getTareas().then((rows) => rows.sort((a, b) => a.Descripcion.localeCompare(b.Descripcion, 'es'))), [refresh])}
      onCreate={createTarea}
      onUpdate={updateTarea}
      onDelete={deleteTarea}
      actions={[
        { label: 'Ver tests', onClick: (row) => navigate(`/admin/tests?tareaId=${row.ID}`) },
      ]}
      filterFn={filterFn}
      toolbar={nav}
    />
  );
}
